export interface SimpleTextIterator {
  next(): SimpleTextIterator;
  value: string;
  done: boolean;
}

// start/end positions of a line / has the line a soft break (=break in the middle of a word)
export type Line = [number, number, boolean?];

// space / minus / soft breaks
export type Breaks = [number, number, number];
const oneMinusBreak: Breaks = [0, 1, 0];
const oneSoftBreak: Breaks = [0, 0, 1];

function updateBreaks(breaks: Breaks, line: string) {
  const end = line[line.length - 1];
  // soft breaks adds softBreak and newline / minus adds a newline / space is replaced by newline
  breaks[end === softBreak ? 2 : end === "-" ? 1 : 0]++;
}

function removeBreaks(breaks: Breaks, toRemove: Breaks) {
  for (let i = 0; i < breaks.length; i++) {
    breaks[i] -= toRemove[i];
  }
}

function textPosFromStringPos(stringsPos: number, breaks: Breaks) {
  return stringsPos - breaksLen(breaks);
}

function breaksLen(breaks: Breaks) {
  return 2 * breaks[2] + breaks[1];
}

export function breakLines(
  text: string,
  width: number,
  measure: (chars: string[], from: number, to: number) => number,
): Line[] {
  const chars = Array.from(text);
  const lines = new Array<Line>();
  let lineStart = 0;
  for (let i = 0; i < chars.length; i++) {
    if (chars[i] === "\n") {
      setLine(i, i + 1);
    } else if (measure(chars, lineStart, i + 1) > width) {
      for (let p = i; ; p--) {
        if (chars[p] === " ") {
          // break before " ", omit " "
          // this means "- " will lose the " " when followed by a long word
          // but keeping the " " would cause long words after "- " to never soft break, which is worse
          setLine(p, p + 1);
          break;
        }
        if (p < i && chars[p] === "-") {
          // break after "-"
          setLine(p + 1, p + 1);
          break;
        }
        if (p <= lineStart) {
          // soft break at current position
          setLine(i, i, true);
          break;
        }
      }
    }
  }
  setLine(chars.length, 0);
  return asStringPositions(chars, lines);

  function setLine(to: number, from: number, soft?: boolean) {
    lines.push(soft ? [lineStart, to, true] : [lineStart, to]);
    lineStart = from;
  }
}

// positions in Lines assume that each character has length 1 (because we use Array.from(s))
// this is not true for strings e.g. "😇".length==2
// so convert the positions back so that they are usable with string functions like substring etc.
function asStringPositions(chars: string[], lines: Line[]) {
  const stringLines = lines.map((line) => line.slice()) as Line[];
  for (let i = 0; i < chars.length; i++) {
    const len = chars[i].length;
    if (len > 1) {
      for (let j = 0; j < lines.length; j++) {
        if (lines[j][0] > i) {
          stringLines[j][0] += len - 1;
        }
        if (lines[j][1] > i) {
          stringLines[j][1] += len - 1;
        }
      }
    }
  }
  return stringLines;
}

export const hardBreak = "\u200c";
export const softBreak = "\u200b";

// apply Lines to the text to get a list of strings
export function stringsFromLines(lines: Line[], text: string): string[] {
  return lines.map(([startPos, endPos]) => text.substring(startPos, endPos));
}

export function withHardBreaks(text: string, enabled: boolean) {
  return enabled ? text.replaceAll("\n", hardBreak + "\n") : text;
}

// apply Lines to the text to get a string with newlines and soft breaks where a word is broken
// additionally, it takes a position in the text and the number of breaks before this position and updates both values.
export function stringFromLines(lines: Line[], text: string, pos: number, breaksBefore: Breaks) {
  const realPos = textPosFromStringPos(pos, breaksBefore);
  let s = "";
  const breaks: Breaks = [0, 0, 0];
  for (let i = 0; i < lines.length - 1; i++) {
    s += text.substring(lines[i][0], lines[i][1]);
    if (lines[i][2]) {
      s += softBreak;
    }
    if (lines[i][1] < realPos) {
      updateBreaks(breaks, s);
    }
    s += "\n";
  }
  s += text.substring(lines[lines.length - 1][0]);
  pos += breaksLen(breaks) - breaksLen(breaksBefore);
  // Math.min is not very elegant (feel free to find a better solution).
  // It's needed because breakLines (see above) can omit the space in "- "
  // which can cause `pos` to go past the end of the string
  return { s, pos: Math.min(s.length, pos), breaks };
}

// return the number of breaks in strings before a given position
export function breaksInStrings(iter: SimpleTextIterator, pos: number): Breaks {
  const breaks: Breaks = [0, 0, 0];
  let len = 0;
  iter = iter.next();
  while (!iter.done) {
    const line = iter.value;
    iter = iter.next();
    len += line.length + 1;
    if (len > pos) {
      break;
    }
    updateBreaks(breaks, line);
  }
  return breaks;
}

// take a list of lines (as produced by breaksToNewlineString.split('\n')) and convert it back to the original text
export function textFromStrings(iter: SimpleTextIterator) {
  let text = "";
  iter = iter.next();
  while (!iter.done) {
    text += iter.value;
    iter = iter.next();
    switch (text[text.length - 1]) {
      case hardBreak:
        text += "\n";
        break;
      case softBreak:
        text = text.substring(0, text.length - 1);
        break;
      case "-":
        // nothing
        break;
      default:
        if (!iter.done) {
          text += " ";
        }
    }
  }
  return text;
}

// delete the newline at newlinePos, update pos and breaksBefore accordingly
export function deleteNewlineInText(
  text: string,
  pos: number,
  breaksBefore: Breaks,
  newlinePos: number | undefined,
) {
  if (newlinePos === undefined) {
    return { text, pos };
  }
  const delPos = textPosFromStringPos(newlinePos, breaksBefore);
  // there's a soft break before the newline -> delete soft break and the previous char also
  if (text.slice(delPos + 1, delPos + 2) === softBreak) {
    return deleteBreaks(oneSoftBreak);
  }
  // there's a "-" before the newline -> delete "-" also
  if (text.slice(delPos, delPos + 1) === "-") {
    return deleteBreaks(oneMinusBreak);
  }
  return { text, pos };

  function deleteBreaks(breaks: Breaks) {
    removeBreaks(breaksBefore, breaks);
    return {
      text: text.slice(0, delPos) + text.slice(delPos + breaksLen(breaks)),
      pos: pos - breaksLen(breaks),
    };
  }
}
